import {
  parse,
  addDays,
  addMonths,
  startOfDay,
  endOfDay,
  isWithinInterval,
  isSameMonth, parseISO
} from 'date-fns'

const CARD_TYPE = 'card'
const COUPON_TYPE = 'coupon'
const WIRE_TRANSFER_TYPE = 'wire transfer'

export const PLAN_NUMBER = {
  trial: 0,
  starter: 21,
  basic: 2,
  light: 22,
  standard: 3,
  premium: 23,
  pro: 4,
  trialOver: 100,
  terminated: 101
}

export const PLAN_NAME = {
  basic: 'Basic',
  standard: 'Standard',
  pro: 'Pro'
}

function getServicePlanNumber (planNumber) {
  // todo: jp, en plan
  switch (planNumber) {
    case PLAN_NUMBER.trial:
      return PLAN_NUMBER.pro
    default:
      return planNumber
  }
}

export function getPlanFeature (planNumber) {
  const options = {
    weeklyReport: false,
    bLogin: false,
    funnels: false,
    sessionReport: false,
    liveHeatmapPdf: false
  }

  if (planNumber === undefined || planNumber === null) {
    return options
  }

  if ([PLAN_NAME.trialOver || PLAN_NUMBER.terminated].includes(planNumber)) {
    return options
  }

  options.weeklyReport = true
  options.bLogin = true
  options.funnels = true
  options.sessionReport = true
  options.liveHeatmapPdf = true

  return options
}

export const normalPlans = [
  {
    planNumber: PLAN_NUMBER.trial,
    name: 'Trial',
    pv: 10000,
    price: 0
  },
  {
    planNumber: PLAN_NUMBER.starter,
    name: 'Starter',
    pv: 50000,
    price: 60000
  },
  {
    planNumber: PLAN_NUMBER.basic,
    name: 'Basic',
    pv: 100000,
    price: 100000
  },
  {
    planNumber: PLAN_NUMBER.light,
    name: 'Light',
    pv: 200000,
    price: 200000
  },
  {
    planNumber: PLAN_NUMBER.standard,
    name: 'Standard',
    pv: 300000,
    price: 300000
  },
  {
    planNumber: PLAN_NUMBER.premium,
    name: 'Premium',
    pv: 400000,
    price: 400000
  },
  {
    planNumber: PLAN_NUMBER.pro,
    name: 'Pro',
    pv: 500000,
    price: 500000
  }
].map(item => {
  return {
    ...item,
    ...getPlanFeature(item.planNumber)
  }
})

const planOptionNames = [
  'pv',
  'dataReflection',
  'availablePages',
  'dataStoragePeriod',
  'heatmap',
  'analytics',
  'comparing',
  'cta',
  'abTesting',
  'excludeCertainElement',
  'excludeCertainIp',
  'weeklyReport',
  'bLogin',
  'funnels',
  'sessionReport',
  'liveHeatmap',
  'liveHeatmapPdf'
]

export const normalPlanOptionList = planOptionNames.map(optionName => {
  let defaultValue

  switch (optionName) {
    case 'dataReflection':
      defaultValue = 1
      break
    case 'availablePages':
      defaultValue = 'Unlimited'
      break
    case 'dataStoragePeriod':
      defaultValue = 6
      break
    case 'heatmap':
    case 'analytics':
    case 'comparing':
    case 'cta':
    case 'abTesting':
    case 'excludeCertainElement':
    case 'excludeCertainIp':
    case 'liveHeatmap':
      defaultValue = true
      break
  }
  const options = {
    name: optionName,
    value: {}
  }

  for (const item of normalPlans) {
    let value = defaultValue

    if (value === undefined) {
      value = item[optionName]
    }
    options.value[item.planNumber] = value
  }

  return options
})

function isWireTransferPlan (plan) {
  const paymentType = plan.paymentType

  if (paymentType === WIRE_TRANSFER_TYPE) {
    return true
  } else if ([CARD_TYPE, COUPON_TYPE].includes(paymentType)) {
    return false
  } else if (plan.bFree) {
    return false
  }
  return true
}

function getNextPlanData (rawData, planNumber) {
  let nextPlan = rawData.nextPlan
  let data = null
  let planInfo = planNumber ? normalPlans.find(e => e.planNumber === planNumber) : null
  let nextPlanInfo = nextPlan.planNumber ? normalPlans.find(e => e.planNumber === nextPlan.planNumber) : null
  if (nextPlan && !nextPlan.bFree) {
    data = {
      planName: nextPlan.planName,
      planNumber: nextPlan.planNumber,
      startDate: parseISO(nextPlan.useStartDate),
      endDate: parseISO(nextPlan.useEndDate),
      isCardPlan: nextPlan.paymentType === CARD_TYPE,
      isWireTransferPlan: isWireTransferPlan(nextPlan),
      isUpgrade: planInfo && nextPlanInfo && nextPlanInfo.pv > planInfo.pv,
      isDowngrade: planInfo && nextPlanInfo && nextPlanInfo.pv < planInfo.pv,
      pv: nextPlanInfo && nextPlanInfo.pv,
      isSameAsCurrent: planInfo && nextPlanInfo && nextPlanInfo.planNumber === planInfo.planNumber
    }
  }
  return data
}

export function getNormalizedPlanData (rawData) {
  const data = {
    hasPlan: false,
    isTrial: false,
    isTrialOver: false,
    isTerminated: false,
    isUnpaid: false,
    useCoupon: false,
    isCardPlan: false,
    isWireTransferPlan: false,
    planName: null,
    planNumber: null,
    servicePlanNumber: null,
    startDate: null,
    isLastMonthlyPlan: false,
    monthlyStartDate: null,
    monthlyEndDate: null,
    endDate: null,
    canCreate: false,
    canChange: false,
    canCancel: false,
    next: null,
    feature: getPlanFeature(null),
    pv: null
  }

  if (!rawData || !rawData.currentPlan) {
    data.canCreate = true
    return data
  }

  const currentPlan = rawData.currentPlan
  const planNumber = currentPlan.planNumber

  if ([PLAN_NUMBER.terminated, PLAN_NUMBER.trialOver].includes(planNumber)) {
    if (planNumber === PLAN_NUMBER.terminated) {
      data.isTerminated = true
    } else if (planNumber === PLAN_NUMBER.trialOver) {
      data.isTrialOver = true
    }
    data.canCreate = true
    data.next = getNextPlanData(rawData)
    return data
  }

  const today = new Date()
  data.planName = currentPlan.planName
  data.planNumber = currentPlan.planNumber
  data.servicePlanNumber = getServicePlanNumber(data.planNumber)
  data.startDate = parseISO(currentPlan.useStartDate)
  data.endDate = parseISO(currentPlan.useEndDate)

  let monthlyStartDate = startOfDay(data.startDate)
  let monthlyEndDate = endOfDay(addDays(addMonths(monthlyStartDate, 1), -1))

  if (isWithinInterval(startOfDay(today), {start: startOfDay(data.startDate), end: endOfDay(data.endDate)})) {
    while (!isWithinInterval(startOfDay(today), {start: monthlyStartDate, end: monthlyEndDate})) {
      monthlyStartDate = addMonths(monthlyStartDate, 1)
      monthlyEndDate = endOfDay(addDays(addMonths(monthlyStartDate, 1), -1))
    }
  } else {
    console.error(`invalid plan date range`)
  }

  data.isLastMonthlyPlan = isSameMonth(startOfDay(data.endDate), monthlyEndDate)

  if (monthlyEndDate > endOfDay(data.endDate)) {
    monthlyEndDate = endOfDay(data.endDate)
  }

  data.monthlyStartDate = monthlyStartDate
  data.monthlyEndDate = monthlyEndDate
  data.monthlyNextStartDate = startOfDay(addDays(data.monthlyEndDate, 1))

  if (planNumber === PLAN_NUMBER.trial) {
    data.hasPlan = true
    data.isTrial = true
    data.canCreate = true
  } else if (currentPlan.paymentStatus === 'unpaid') {
    data.hasPlan = true
    data.isUnpaid = true
    data.canCancel = true
  } else if (currentPlan.paymentType === COUPON_TYPE) {
    data.hasPlan = true
    data.useCoupon = true
    data.canChange = true
    data.next = getNextPlanData(rawData, planNumber)

    if (data.next && data.next.isWireTransferPlan) {
      data.canChange = false
    }
  } else if (!currentPlan.bFree) {
    data.hasPlan = true
    data.isCardPlan = currentPlan.paymentType === CARD_TYPE
    data.isWireTransferPlan = isWireTransferPlan(currentPlan)
    data.next = getNextPlanData(rawData, planNumber)

    if (data.isCardPlan) {
      if (!data.next || data.next.isCardPlan) {
        data.canChange = true
        data.canCancel = true
      }
    }
  } else {
    throw new Error('invalid plan')
  }

  let planInfo = data.planNumber ? normalPlans.find(e => e.planNumber === data.planNumber) : null
  if (planInfo) {
    data.pv = planInfo.pv
  }

  data.feature = getPlanFeature(data.planNumber)

  return data
}
